// ============================================================================
// Egyptian payroll tax math — ported 1:1 from the backend's authoritative
// implementation (user-service/src/services/payrollService.js:
// calculateNetFromGross / calculateGrossFromNet) so the export's numbers
// never diverge from what the system actually pays people. Do not hand-edit
// the bracket thresholds here without also updating the backend — they must
// stay identical.
//
// The one NEW piece for the export is `marginalTaxImpact` below: a single,
// generic function that answers "if this employee's net take-home changes by
// X EGP because of ONE allowance/deduction line, how much of that swing is
// income tax vs Martyrs' Fund?" — the same question for every allowance and
// deduction type, taxable or not. There is no per-type branching anywhere in
// this file; callers decide whether to invoke it at all based on that line's
// own isTaxable/isPreTax flag, and the math is identical regardless of
// whether the line is "Transportation", "Eid Bonus", or "Penalty".
// ============================================================================

const INSURANCE_EMPLOYEE_SHARE_RATE = 0.11;
const MARTYRS_FUND_RATE = 0.0005;

// gross → { netSalary, monthlyTax, martyrsFund, employeeInsuranceShare }
const calculateNetFromGross = (gross, insSalary) => {
  const empShare = (Number(insSalary) || 0) * INSURANCE_EMPLOYEE_SHARE_RATE;
  const netSalaryBeforeTaxes = gross - empShare;
  const annualSalaryBefore = netSalaryBeforeTaxes * 12;

  const annualAfterThreshold =
    annualSalaryBefore <= 20000 ? 0 : annualSalaryBefore - 20000;

  let annualTaxes = 0;
  if (annualAfterThreshold <= 40000) {
    annualTaxes = 0;
  } else if (annualAfterThreshold <= 55000) {
    annualTaxes = (annualAfterThreshold - 40000) * 0.1;
  } else if (annualAfterThreshold <= 70000) {
    annualTaxes = (annualAfterThreshold - 55000) * 0.15 + 1500;
  } else if (annualAfterThreshold <= 200000) {
    annualTaxes = (annualAfterThreshold - 70000) * 0.2 + 1500 + 2250;
  } else if (annualAfterThreshold <= 400000) {
    annualTaxes =
      (annualAfterThreshold - 200000) * 0.225 + 1500 + 2250 + 26000;
  } else {
    annualTaxes =
      (annualAfterThreshold - 400000) * 0.25 + 1500 + 2250 + 26000 + 45000;
  }

  const monthlyTax = annualTaxes / 12;
  const martyrsFund = gross * MARTYRS_FUND_RATE;
  const netSalary = netSalaryBeforeTaxes - (monthlyTax + martyrsFund);

  return { netSalary, monthlyTax, martyrsFund, employeeInsuranceShare: empShare };
};

// net → gross, the numeric inverse of calculateNetFromGross (binary search —
// the bracket function isn't algebraically invertible in one line because of
// the Martyrs' Fund term feeding back into the effective rate).
const calculateGrossFromNet = (targetNet, insSalary) => {
  let low = targetNet;
  let high = targetNet * 3 + 10000;
  for (let i = 0; i < 200; i++) {
    const mid = (low + high) / 2;
    const { netSalary } = calculateNetFromGross(mid, insSalary);
    if (Math.abs(netSalary - targetNet) < 0.005) return mid;
    if (netSalary < targetNet) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
};

// The generic per-line calculator described above. `netDelta` is the line's
// own net-equivalent impact (positive for an allowance, negative for a
// pre-tax deduction) and `baseGross` is whatever gross figure applies BEFORE
// this line is folded in. Returns how much additional gross that net swing
// required, and how that gross split between income tax and Martyrs' Fund.
const marginalTaxImpact = (baseGross, insSalary, netDelta) => {
  if (!netDelta) return { grossDelta: 0, taxDelta: 0, martyrsFundDelta: 0 };

  const baseline = calculateNetFromGross(baseGross, insSalary);
  const targetNet = baseline.netSalary + netDelta;

  let low = netDelta >= 0 ? 0 : netDelta * 3 - 10000;
  let high = netDelta >= 0 ? netDelta * 3 + 10000 : 0;
  let grossDelta = netDelta;

  for (let i = 0; i < 200; i++) {
    const mid = (low + high) / 2;
    const { netSalary } = calculateNetFromGross(baseGross + mid, insSalary);
    grossDelta = mid;
    if (Math.abs(netSalary - targetNet) < 0.005) break;
    if (netSalary < targetNet) low = mid;
    else high = mid;
  }

  const after = calculateNetFromGross(baseGross + grossDelta, insSalary);
  return {
    grossDelta,
    taxDelta: after.monthlyTax - baseline.monthlyTax,
    martyrsFundDelta: after.martyrsFund - baseline.martyrsFund,
  };
};

// The inverse of marginalTaxImpact: given the NET impact a line had, recover
// the GROSS amount that produced it on top of `baseGross`. Mirrors
// recoverGrossFromNetDelta in payrollService.js — the backend stores only the
// converted net for a Gross-entered line, so this is how the export gets back
// to the figure the client actually typed (Viking's "Over Time Gross", for
// instance). Monotonic in gross, so a plain bisection converges.
const recoverGrossFromNetDelta = (baseGross, insSalary, netDelta) => {
  if (!netDelta || netDelta <= 0 || !baseGross || baseGross <= 0) return 0;
  const baseNet = calculateNetFromGross(baseGross, insSalary).netSalary;
  let lo = 0;
  let hi = Math.max(netDelta * 4, 1000);
  for (let i = 0; i < 40; i++) {
    const netAt = calculateNetFromGross(baseGross + hi, insSalary).netSalary - baseNet;
    if (netAt >= netDelta) break;
    hi *= 2;
  }
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const netAt = calculateNetFromGross(baseGross + mid, insSalary).netSalary - baseNet;
    if (Math.abs(netAt - netDelta) < 0.0005) return mid;
    if (netAt < netDelta) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
};

export {
  INSURANCE_EMPLOYEE_SHARE_RATE,
  MARTYRS_FUND_RATE,
  calculateNetFromGross,
  calculateGrossFromNet,
  marginalTaxImpact,
  recoverGrossFromNetDelta,
};
